import { useEffect, useState } from "react";

import { api } from "../api/client";
import { useApiList, useMutation } from "../api/hooks";
import type { ImportJob, ImportTemplate } from "../api/types";
import { Badge, DataTable, fmtDate, Page, useToast } from "../components/ui";
import type { UploadTicket as Ticket } from "../api/types";

/** CSV imports (02 §16): upload the file as an asset, queue the job, watch
 * the validation report. Excel users export a sheet as CSV. */
export function ImportsPage() {
  const [tick, setTick] = useState(0);
  const templates = useApiList<ImportTemplate & { id: string }>("/admin/imports/templates", {});
  const jobs = useApiList<ImportJob>("/admin/imports", { _t: tick });
  const [kind, setKind] = useState("vendors");
  const [file, setFile] = useState<File | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const { busy, run } = useMutation();
  const toast = useToast();

  // Poll while something is queued or running.
  useEffect(() => {
    if (!jobs.data.some((j) => j.status === "queued" || j.status === "running")) return;
    const id = setTimeout(() => setTick((t) => t + 1), 3000);
    return () => clearTimeout(id);
  }, [jobs.data]);

  async function start() {
    if (!file) return;
    try {
      const { data: ticket } = await api<Ticket>("/media/uploads", {
        method: "POST",
        body: { owner_type: "asset", collection: "imports", filename: file.name, mime: "text/csv", size_bytes: file.size },
      });
      const put = await fetch(ticket.upload_url, { method: ticket.method, headers: Object.fromEntries(ticket.headers), body: file });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      await api("/media/complete", { method: "POST", body: { storage_key: ticket.storage_key } });
      const r = await run("/admin/imports", { method: "POST", body: { kind, storage_key: ticket.storage_key } });
      if (r.ok) {
        toast("Import queued");
        setFile(null);
        setTick((t) => t + 1);
      } else toast(r.error, "err");
    } catch (e) {
      toast(e instanceof Error ? e.message : "upload failed", "err");
    }
  }

  const template = templates.data.find((t) => t.kind === kind);

  return (
    <Page title="Imports">
      <div className="grid">
        <section className="card">
          <h2>New import</h2>
          <div className="form">
            <label className="field">
              <span className="field-label">What to import</span>
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
                {templates.data.map((t) => (
                  <option key={t.kind} value={t.kind}>
                    {t.kind}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">CSV file</span>
              <input className="input" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              {template && (
                <small className="muted">
                  Header row: <code className="mono">{template.columns.join(",")}</code> (required: {template.required.join(", ")}; booleans yes/no; ids are UUIDs from the lists)
                </small>
              )}
            </label>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" disabled={!file || busy} onClick={start}>
                {busy ? "Starting…" : "Upload and import"}
              </button>
            </div>
          </div>
        </section>
      </div>
      <h2 style={{ marginTop: 24 }}>Jobs</h2>
      <DataTable
        columns={[
          { key: "created_at", label: "Started", render: (j) => fmtDate(j.created_at) },
          { key: "kind", label: "Kind" },
          {
            key: "status",
            label: "Status",
            render: (j) => <Badge tone={j.status === "done" ? (j.failed ? "warn" : "ok") : j.status === "failed" ? "bad" : "info"}>{j.status}</Badge>,
          },
          { key: "imported", label: "Imported", align: "right", render: (j) => `${j.imported} / ${j.total}` },
          { key: "failed", label: "Failed", align: "right" },
          {
            key: "errors",
            label: "Report",
            render: (j) =>
              j.error ? (
                <span className="error">{j.error}</span>
              ) : j.errors.length === 0 ? (
                j.status === "done" ? "clean" : "—"
              ) : open === j.id ? (
                <ul className="report">
                  {j.errors.map((e) => (
                    <li key={e.row}>
                      <span className="mono">row {e.row}</span> · {e.error}
                    </li>
                  ))}
                </ul>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(j.id)}>
                  Show {j.errors.length} error{j.errors.length === 1 ? "" : "s"}
                </button>
              ),
          },
          { key: "finished_at", label: "Finished", render: (j) => fmtDate(j.finished_at) },
        ]}
        rows={jobs.data}
        loading={jobs.loading && jobs.data.length === 0}
        error={jobs.error}
        empty="No imports yet"
      />
    </Page>
  );
}
